'use client';

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';
import { ServiceCardData } from './ServiceCard';

interface ServiceRailProps {
  services: ServiceCardData[];
  selectedId: string;
  onSelect: (id: string) => void;
  /** How many services to show in the collapsed rail (rest go under "+N More"). */
  popularCount?: number;
}

function ServiceChip({
  service,
  selected,
  onSelect,
}: {
  service: ServiceCardData;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <motion.button
      type="button"
      layout
      onClick={onSelect}
      whileHover={{ y: -1, scale: 1.01 }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 420, damping: 26 }}
      className={`relative flex items-center gap-2 rounded-xl px-3 py-2 border text-left transition-colors duration-200 ${
        selected
          ? 'border-purple-500/70 bg-gradient-to-br from-purple-600/25 to-purple-500/10 shadow-[0_0_0_1px_rgba(168,85,247,0.35),0_6px_22px_-8px_rgba(124,58,237,0.6)]'
          : 'border-white/[0.08] bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.05]'
      }`}
    >
      {selected && (
        <motion.span
          layoutId="service-glow"
          className="pointer-events-none absolute -inset-4 bg-purple-600/25 blur-xl -z-10"
        />
      )}
      <span
        className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
          selected ? 'bg-purple-500/25 text-purple-100' : 'bg-white/[0.05] text-gray-400'
        }`}
      >
        <Icon name={service.icon} size={15} variant="outline" />
      </span>
      <span className={`text-[13px] font-semibold whitespace-nowrap ${selected ? 'text-white' : 'text-gray-300'}`}>
        {service.name}
      </span>
      <AnimatePresence>
        {selected && (
          <motion.span
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 500, damping: 22 }}
            className="w-4 h-4 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0"
          >
            <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3.5} d="M5 13l4 4L19 7" />
            </svg>
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  );
}

export default function ServiceRail({
  services,
  selectedId,
  onSelect,
  popularCount = 6,
}: ServiceRailProps) {
  const [expanded, setExpanded] = useState(false);

  const popular = services.slice(0, popularCount);
  const more = services.slice(popularCount);
  const hasMore = more.length > 0;
  // If the selected service is hidden in "more", surface it so it's always visible.
  const selectedInMore = more.some((s) => s.id === selectedId);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {popular.map((s) => (
          <ServiceChip
            key={s.id}
            service={s}
            selected={s.id === selectedId}
            onSelect={() => onSelect(s.id)}
          />
        ))}

        {/* Surface a selected "more" service as an extra visible chip */}
        {selectedInMore && !expanded && (
          <ServiceChip
            key={selectedId}
            service={more.find((s) => s.id === selectedId)!}
            selected
            onSelect={() => onSelect(selectedId)}
          />
        )}

        {hasMore && (
          <motion.button
            type="button"
            whileTap={{ scale: 0.97 }}
            onClick={() => setExpanded((v) => !v)}
            className={`flex items-center gap-1.5 rounded-xl px-3 py-2 border text-[13px] font-semibold transition-colors ${
              expanded
                ? 'border-purple-500/40 bg-purple-500/[0.08] text-purple-200'
                : 'border-white/[0.08] bg-white/[0.02] text-gray-400 hover:text-gray-200 hover:border-white/20'
            }`}
          >
            +{more.length} More
            <motion.span animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
              <Icon name="ChevronDownIcon" size={14} variant="outline" />
            </motion.span>
          </motion.button>
        )}
      </div>

      <AnimatePresence initial={false}>
        {expanded && hasMore && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <div className="mt-3 pt-3 border-t border-white/[0.06]">
              <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-2 font-semibold">
                All Services
              </p>
              <div className="flex flex-wrap gap-2">
                {more.map((s) => (
                  <ServiceChip
                    key={s.id}
                    service={s}
                    selected={s.id === selectedId}
                    onSelect={() => onSelect(s.id)}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
